import { useCallback, useEffect, useState } from "react";
import { Wallet, Link2, Plus } from "lucide-react";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import AllocateReceiptDialog from "@/components/collection/AllocateReceiptDialog";

const STATUS_LABEL = {
  belum_dibayar: { label: "Belum Dibayar", cls: "bg-amber-100 text-amber-800" },
  dibayar_sebagian: { label: "Dibayar Sebagian", cls: "bg-indigo-100 text-indigo-800" },
  lunas: { label: "Lunas", cls: "bg-emerald-100 text-emerald-800" },
  perlu_verifikasi: { label: "Perlu Verifikasi", cls: "bg-rose-100 text-rose-800" },
};

/**
 * 2026-10-19 (user request "KLAIM RETENSI MENUNGGU PEMBAYARAN"): rincian klaim
 * retensi di detail drawer Collection — Bruto / PPh-Potongan / Net / Diterima /
 * Sisa / Status + invoice sumber + daftar pembayaran (receipt) & alokasi.
 * Angka diambil dari GET /api/ar/retention-claim-widget (engine A.R.) — TIDAK
 * dihitung ulang dengan rumus frontend berbeda (aturan user).
 */
export default function ClaimPaymentDetail({ row, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [allocOpen, setAllocOpen] = useState(false);

  const load = useCallback(() => {
    if (!row?.project_id || !row?.entry_id) return;
    api.get("/ar/retention-claim-widget", { params: { project_id: row.project_id } })
      .then(({ data }) => setDetail((data?.rows || []).find((r) => r.entry_id === row.entry_id) || null))
      .catch(() => setDetail(null));
  }, [row?.project_id, row?.entry_id]);

  useEffect(() => { load(); }, [load]);

  const st = STATUS_LABEL[detail?.claim_status] || null;
  const receipts = detail?.receipts || [];
  const extraAllocs = detail?.extra_allocations || [];

  return (
    <div className="border border-purple-200 bg-purple-50/40 p-3 space-y-3" data-testid="claim-payment-detail">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider text-purple-800 font-semibold flex items-center gap-1.5">
          <Wallet size={12} /> Rincian Klaim Retensi
        </div>
        {st && (
          <span className={`px-2 py-0.5 text-[10px] font-semibold ${st.cls}`} data-testid="claim-detail-status">{st.label}</span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
        <div className="border border-slate-200 bg-white p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Nilai Bruto</div>
          {/* 2026-10-20 (user request "SINKRONKAN COUNTER STATUS DAN DATA
              KLAIM LAMA"): Bruto HANYA dari field sumber klaim (gross dari
              engine). Jika sumber tidak dapat dipastikan → "—" + badge
              Nominal Perlu Verifikasi. Tidak lagi fallback paid+outstanding. */}
          {((row.claim_bruto_known ?? (detail?.gross > 0)) && (detail?.gross ?? row.claim_gross_amount) > 0) ? (
            <div className="text-sm tabular-nums font-semibold text-slate-800" data-testid="claim-detail-gross">{formatIDR(detail?.gross ?? row.claim_gross_amount)}</div>
          ) : (
            <div className="flex flex-col gap-0.5" data-testid="claim-detail-gross-unknown">
              <div className="text-sm tabular-nums font-semibold text-slate-400">—</div>
              <div className="text-[9px] font-semibold uppercase tracking-wider text-rose-700">Nominal Perlu Verifikasi</div>
            </div>
          )}
        </div>
        <div className="border border-slate-200 bg-white p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">PPh / Potongan</div>
          <div className="text-sm tabular-nums font-semibold text-sky-800" data-testid="claim-detail-pph">− {formatIDR(detail?.pph ?? row.pph_amount ?? 0)}</div>
        </div>
        <div className="border border-slate-200 bg-white p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Kas Harus Diterima (Net)</div>
          {/* R225: neto klaim = bruto klaim − potongan klaim. "—" bila nominal
              tidak dapat dipastikan; nilai invoice sumber DILARANG tampil. */}
          {(row.claim_bruto_known === false) ? (
            <div className="text-sm tabular-nums font-semibold text-slate-400" data-testid="claim-detail-net">—</div>
          ) : (
            <div className="text-sm tabular-nums font-semibold text-[#0B1D3A]" data-testid="claim-detail-net">{formatIDR(detail?.cash_due ?? row.cash_due ?? 0)}</div>
          )}
        </div>
        <div className="border border-slate-200 bg-white p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Sudah Diterima</div>
          <div className="text-sm tabular-nums font-semibold text-emerald-700" data-testid="claim-detail-received">{formatIDR(detail?.received ?? row.paid_amount ?? 0)}</div>
        </div>
        <div className="border border-slate-200 bg-white p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Sisa Klaim</div>
          <div className={`text-sm tabular-nums font-bold ${(detail?.remaining ?? row.outstanding) > 0 ? "text-rose-700" : "text-slate-500"}`} data-testid="claim-detail-remaining">
            {formatIDR(detail?.remaining ?? row.outstanding ?? 0)}
          </div>
        </div>
        {(detail?.overpaid_excess ?? 0) > 0 && (
          <div className="border border-rose-300 bg-rose-50 p-2">
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Kelebihan Pembayaran</div>
            <div className="text-sm tabular-nums font-bold text-rose-800" data-testid="claim-detail-overpaid">{formatIDR(detail.overpaid_excess)}</div>
            <div className="text-[9px] text-rose-600">Ditampilkan terpisah — ditandai Perlu Verifikasi</div>
          </div>
        )}
      </div>

      <div className="text-[10px] text-slate-600 flex items-center gap-1.5 flex-wrap">
        <Link2 size={11} className="text-slate-400" />
        <span>Invoice Sumber Retensi:</span>
        {row.retention_source_entry_id ? (
          <span className="font-mono font-semibold text-emerald-700" data-testid="claim-detail-source">{row.retention_source_entry_id.slice(0, 8)}…</span>
        ) : (
          <span className="font-semibold text-rose-700" data-testid="claim-detail-source-missing">Belum tertaut — gunakan tombol "Perlu Verifikasi · Tautkan" di tabel</span>
        )}
      </div>

      {/* 2026-10-20 (user request): "Pembayaran berasal dari data historis/
          legacy dan belum memiliki rincian alokasi" — bila entry menyimpan
          nilai legacy (net_received_amount/net_amount) tanpa receipt/alokasi
          khusus klaim. Nilai itu TIDAK dihitung sebagai Sudah Diterima; status
          klaim tetap Perlu Verifikasi sampai FAT mengisi receipt/alokasi. */}
      {(detail?.legacy_payment_hint ?? 0) > 0 && (
        <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid="claim-detail-legacy-hint">
          Pembayaran berasal dari data historis/legacy dan belum memiliki rincian alokasi. Nilai tercatat <b className="tabular-nums">{formatIDR(detail.legacy_payment_hint)}</b> tidak otomatis dihitung sebagai Sudah Diterima dan tidak menjadikan klaim ini Lunas. FAT perlu melengkapi receipt/alokasi khusus klaim retensi; status klaim tetap <b>Perlu Verifikasi</b>.
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pembayaran & Alokasi ({receipts.length + extraAllocs.length})</div>
          <button type="button" data-testid="btn-allocate-receipt"
            onClick={() => setAllocOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] uppercase tracking-wider font-semibold">
            <Plus size={11} /> Alokasikan Pembayaran
          </button>
        </div>
        {receipts.length === 0 && extraAllocs.length === 0 ? (
          <div className="text-center py-3 text-slate-500 text-xs italic bg-white border border-slate-200">Belum ada pembayaran tercatat untuk klaim ini.</div>
        ) : (
          <div className="space-y-1.5" data-testid="claim-receipt-list">
            {receipts.map((r) => (
              <div key={r.receipt_id} className={`border px-3 py-2 bg-white text-xs ${r.complete ? "border-slate-200" : "border-rose-300"}`} data-testid={`claim-receipt-${r.receipt_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="tabular-nums font-semibold text-emerald-700">{formatIDR(r.allocated_to_this_claim)}</span>
                  <span className="text-slate-500">{r.date ? fmtDate(r.date) : "tanpa tanggal"}</span>
                  {!r.complete && (
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-rose-100 text-rose-800" data-testid={`receipt-incomplete-${r.receipt_id}`}>
                      Bukti Pembayaran Belum Lengkap
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {[r.bank_name, r.account_no, r.transfer_ref].filter(Boolean).join(" · ") || "referensi belum diisi"}
                  {r.unallocated_remainder > 0.005 && <span className="ml-1 text-amber-700">· sisa belum dialokasi {formatIDR(r.unallocated_remainder)}</span>}
                </div>
              </div>
            ))}
            {extraAllocs.map((a) => (
              <div key={`${a.receipt_id}-${a.amount}`} className="border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-xs" data-testid={`claim-extra-alloc-${a.receipt_id}`}>
                <span className="tabular-nums font-semibold text-emerald-700">{formatIDR(a.amount)}</span>
                <span className="text-slate-500"> · dialokasikan dari receipt {a.receipt_id?.slice(0, 8)}… {a.date ? `· ${fmtDate(a.date)}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <AllocateReceiptDialog
        open={allocOpen}
        onOpenChange={setAllocOpen}
        claim={row}
        onSaved={() => { load(); onChanged?.(); }}
      />
    </div>
  );
}
